import { useState, useRef, useEffect } from 'react';
import ListItem from '@tiptap/extension-list-item';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import {
  MenuAltIcon,
  LinkIcon as LinkInputIcon
} from '../../../icons/SvgIcons';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link as LinkIcon,
  Unlink
} from 'lucide-react';
import { RichTextAreaProps } from './RichTextArea.types';
import { Input } from '../Input/Input';
import { TextButton } from '../Button/Buttons';

import './RichTextAreaStyles.css';

export function RichTextArea({
  label,
  placeholder,
  value,
  className,
  onChange
}: RichTextAreaProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isToolbarActive, setIsToolbarActive] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [hadSelection, setHadSelection] = useState(false);
  const [, setEditorStateVersion] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLDivElement>(null);
  const lastSelectionRef = useRef<{ from: number; to: number } | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        listItem: false
      }),
      BulletList,
      OrderedList,
      ListItem,
      Underline,
      Link.configure({
        openOnClick: false
      }),
      TextAlign.configure({
        types: [
          'heading',
          'paragraph',
          'bulletList',
          'orderedList',
          'listItem'
        ],
        alignments: ['left', 'center', 'right'],
        defaultAlignment: 'left'
      }),
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: true,
        includeChildren: true
      })
    ],
    content: value,
    editorProps: {
      attributes: {
        class:
          'py-[9px] px-[13px] mx-[1px] resize-y rounded-md border border-gray-200 bg-white shadow-sm hover:border hover:border-purple-800 focus:outline-purple-700 text-gray-900 placeholder:text-gray-550'
      },
      // block emoji or symbol character
      handleTextInput: (_view, _from, _to, text) => {
        return /(?:[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27BF])/.test(text);
      },
      handlePaste: (_view, event) => {
        const text = event.clipboardData?.getData('text/plain') ?? '';
        if (!text) return false;
        return /(?:[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27BF])/.test(text);
      }
    },
    onFocus: () => setIsFocused(true)
  });

  const selectionContainsLink = (): boolean => {
    const { from, to } = editor.state.selection;
    let containsLink = false;

    editor.state.doc.nodesBetween(from, to, (node) => {
      if (node.marks?.some((mark) => mark.type.name === 'link')) {
        containsLink = true;
      }
    });

    return containsLink;
  };

  const isLinkActive = editor.isActive('link') || selectionContainsLink();

  const disableApplyButton =
    !linkUrl.trim() ||
    (!linkText.trim() &&
      !editor.state.doc
        .textBetween(
          editor.state.selection.from,
          editor.state.selection.to,
          ' '
        )
        .trim());

  // sync toolbar state with editor state
  useEffect(() => {
    if (!editor) return;

    const update = () => setEditorStateVersion((prev) => prev + 1);

    editor.on('selectionUpdate', update);
    editor.on('transaction', update);
    editor.on('update', update);

    return () => {
      editor.off('selectionUpdate', update);
      editor.off('transaction', update);
      editor.off('update', update);
    };
  }, [editor]);

  // propagate content changes
  useEffect(() => {
    if (!editor) return;

    const update = () => {
      const plainText = editor
        .getText()
        .replace(/\u00A0/g, ' ')
        .trim();
      const html = plainText.length === 0 ? '' : editor.getHTML();

      onChange(html);
    };

    editor.on('update', update);

    return () => {
      editor.off('update', update);
    };
  }, [editor, onChange]);

  // Keep editor content in sync with external form state.
  // Without this, the editor can display stale text even when the stored value changed.
  useEffect(() => {
    if (!editor) return;

    const nextValue = value ?? '';
    const editorText = editor.getText().replace(/\u00A0/g, ' ').trim();
    const nextValueText = nextValue
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

    const editorHtml = editorText.length === 0 ? '' : editor.getHTML();
    if (editorHtml === nextValue) return;
    if (editorText.length === 0 && nextValueText.length === 0) return;

    editor.commands.setContent(nextValue);
  }, [editor, value]);

  // hide toolbar or link modal when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (linkInputRef.current && !linkInputRef.current.contains(target)) {
        setShowLinkInput(false);
      }

      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        setIsFocused(false);
        setIsToolbarActive(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!editor) return null;

  const setLink = () => {
    setLinkUrl('');
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');

    // if link already exists, unset it
    if (isLinkActive) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    lastSelectionRef.current = { from, to };
    setHadSelection(!!selectedText);
    setLinkText(selectedText);
    setLinkUrl(editor.getAttributes('link').href || '');
    setShowLinkInput(true);
  };

  const applyLink = () => {
    const { from, to } = lastSelectionRef.current || {};
    const link = linkUrl.trim();

    if (!link) {
      editor.chain().focus().unsetLink().run();
      setShowLinkInput(false);
      return;
    }

    // selected text exist
    if (from !== undefined && to !== undefined && from !== to) {
      editor.commands.focus();
      editor.commands.setTextSelection({ from, to });

      editor.chain().extendMarkRange('link').setLink({ href: link }).run();

      editor.commands.setTextSelection(to);
      editor.chain().focus().unsetMark('link').run();
    } else {
      // no selection
      const textToInsert = linkText || link;
      editor
        .chain()
        .focus()
        .insertContent(
          `<a href="${link}" class="underline-custom">${textToInsert}</a> `
        )
        .focus('end')
        .unsetLink()
        .run();
    }

    setShowLinkInput(false);
    setLinkUrl('');
    setLinkText('');
    setHadSelection(false);
    lastSelectionRef.current = null;
  };

  const reapplyAlign = () => {
    const currentAlign =
      editor.getAttributes('paragraph')?.textAlign ||
      editor.getAttributes('heading')?.textAlign ||
      'left';

    editor.chain().focus().setTextAlign(currentAlign).run();
  };

  return (
    <div
      ref={wrapperRef}
      className={`${className} ${isToolbarActive ? 'has-toolbar' : ''}`}
    >
      <p className="text-sm text-gray-800 font-medium mb-2">{label}</p>

      {(isFocused || isToolbarActive) && (
        <div className="editor-toolbar flex flex-wrap items-center gap-2 rounded-t-lg px-2 py-1 bg-white transition-all">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-1.5 rounded hover:bg-gray-40 ${
              editor.isActive('bold') ? 'bg-gray-100' : ''
            }`}
          >
            <Bold size={14} color="#4E4E56" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-1.5 rounded hover:bg-gray-40 ${
              editor.isActive('italic') ? 'bg-gray-100' : ''
            }`}
          >
            <Italic size={14} color="#4E4E56" />
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={`p-1.5 rounded hover:bg-gray-40 ${
              editor.isActive('underline') ? 'bg-gray-100' : ''
            }`}
          >
            <UnderlineIcon size={14} color="#4E4E56" />
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={`p-1.5 rounded hover:bg-gray-40 ${
              editor.isActive({ textAlign: 'left' }) ? 'bg-gray-100' : ''
            }`}
          >
            <AlignLeft size={14} color="#4E4E56" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={`p-1.5 rounded hover:bg-gray-40 ${
              editor.isActive({ textAlign: 'center' }) ? 'bg-gray-100' : ''
            }`}
          >
            <AlignCenter size={14} color="#4E4E56" />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={`p-1.5 rounded hover:bg-gray-40 ${
              editor.isActive({ textAlign: 'right' }) ? 'bg-gray-100' : ''
            }`}
          >
            <AlignRight size={14} color="#4E4E56" />
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          <button
            onClick={() => {
              editor.chain().focus().toggleBulletList().run();
              reapplyAlign();
            }}
            className={`p-1.5 rounded hover:bg-gray-40 ${
              editor.isActive('bulletList') ? 'bg-gray-100' : ''
            }`}
          >
            <List size={14} color="#4E4E56" />
          </button>
          <button
            onClick={() => {
              editor.chain().focus().toggleOrderedList().run();
              reapplyAlign();
            }}
            className={`p-1.5 rounded hover:bg-gray-40 ${
              editor.isActive('orderedList') ? 'bg-gray-100' : ''
            }`}
          >
            <ListOrdered size={14} color="#4E4E56" />
          </button>

          <div className="w-px h-6 bg-gray-300 mx-1" />

          <button
            disabled={showLinkInput}
            onClick={setLink}
            className={`p-1.5 rounded hover:bg-gray-40 ${
              isLinkActive ? 'bg-gray-100' : ''
            }`}
          >
            {isLinkActive ? (
              <Unlink size={14} color="#4E4E56" />
            ) : (
              <LinkIcon size={14} color="#4E4E56" />
            )}
          </button>
        </div>
      )}

      <EditorContent editor={editor} />

      {showLinkInput && (
        <div
          ref={linkInputRef}
          className="absolute z-10 w-[512px] flex flex-col gap-6 p-6 rounded-lg border border-gray-100 shadow-xl bg-white"
        >
          {!hadSelection && (
            <Input
              id="text"
              label=""
              maxLength={255}
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              placeholder="Text"
              inputClassName="input !text-zinc-900 !border-gray-200 hover:!border-purple-800 focus:!ring-purple-700"
              icon={MenuAltIcon()}
            />
          )}

          <Input
            id="url"
            label=""
            maxLength={255}
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="Type or paste link"
            inputClassName="input !text-zinc-900 !border-gray-200 hover:!border-purple-800 focus:!ring-purple-700"
            icon={LinkInputIcon()}
          />

          <div className="flex justify-end">
            <TextButton
              onClick={applyLink}
              disabled={disableApplyButton}
              className="disabled:!text-gray-200"
            >
              Apply
            </TextButton>
          </div>
        </div>
      )}
    </div>
  );
}
